import { Tooltip } from '@mui/material';
import Box, { type BoxProps } from '@mui/material/Box';
import { varAlpha } from 'minimal-shared/utils';

import { SvgColor } from '@infor/ui-lib/src/components/svg-color';
import { useWindowSize } from '@infor/utils/src/hooks/use-window-size';

import type { SettingsState } from '../types';

import { CONFIG } from './../../../config-global';
import { OptionButton } from './styles';

// ----------------------------------------------------------------------

export type NavLayoutOptionProps = BoxProps & {
  value: SettingsState['navLayout'];
  options: SettingsState['navLayout'][];
  onChangeOption: (newOption: SettingsState['navLayout']) => void;
};

export function NavLayoutOptions({
  sx,
  value,
  options,
  onChangeOption,
  ...other
}: NavLayoutOptionProps) {
  const { width } = useWindowSize();

  const isShortScreen = width < 1200;

  return (
    <Box
      sx={[
        () => ({
          gap: 1.5,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {options.map((option) => {
        const selected = value === option;
        const showTooltipAlert = isShortScreen && option === 'vertical';

        return (
          <Tooltip
            key={option}
            title={
              showTooltipAlert
                ? 'Disponível apenas para telas maiores que 1200px'
                : ''
            }
          >
            <span>
              <OptionButton
                selected={selected}
                onClick={() => onChangeOption(option)}
                disabled={showTooltipAlert}
                sx={[
                  (theme) => ({
                    height: 64,
                    border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
                  }),
                ]}
              >
                <SvgColor
                  src={`${CONFIG.assetsDir}/assets/icons/settings/ic-nav-${option}.svg`}
                  sx={{ width: 1, height: 1, color: 'currentColor' }}
                />
              </OptionButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}

// ----------------------------------------------------------------------

export type NavColorOptionProps = BoxProps & {
  value: SettingsState['navColor'];
  options: SettingsState['navColor'][];
  onChangeOption: (newOption: SettingsState['navColor']) => void;
};

export function NavColorOptions({
  sx,
  value,
  options,
  onChangeOption,
  ...other
}: NavColorOptionProps) {
  return (
    <Box
      sx={[
        () => ({
          gap: 1.5,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
        }),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {options.map((option) => {
        const selected = value === option;

        return (
          <OptionButton
            key={option}
            selected={selected}
            onClick={() => onChangeOption(option)}
            sx={{ gap: 1.5, height: 56, textTransform: 'capitalize' }}
          >
            <SvgColor
              src={`${CONFIG.assetsDir}/assets/icons/settings/ic-sidebar-${option === 'integrate' ? 'outline' : 'filled'}.svg`}
            />
            {option === 'integrate' ? 'Integrado' : 'Aparente'}
          </OptionButton>
        );
      })}
    </Box>
  );
}
